import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Users, Copy, Trash2, Heart, X, Ticket, UserCheck, Check, ChevronRight, Eye, Flag, Ban, Building2, MapPin, ShieldCheck, Mail, Phone, MessageCircle } from "lucide-react";

const statusBadge = { pending: "bg-amber-100 text-amber-800", active: "bg-emerald-100 text-emerald-800", accepted: "bg-emerald-100 text-emerald-800", revoked: "bg-slate-200 text-slate-600" };
const HELPING_LABEL = { son: "My son", daughter: "My daughter", family: "A family member", client: "A client" };
const ROLE_LABEL = { wali: "Wali / Parent", imam: "Imam", registrar: "Islamic Marriage Registrar / Officiant", officiant: "Islamic Marriage Registrar / Officiant", parent: "Parent" };

const reqName = (r) => r?.requester?.displayName || r?.byName || r?.requesterName || "A guardian / imam / registrar";
const reqRole = (r) => ROLE_LABEL[r?.requester?.assistantRole] || r?.byRole || r?.requester?.assistantRole || "Assistant";
const reqPhoto = (r) => r?.requester?.photoBase64 || r?.requester?.photo || null;
const reqId = (r) => r?.requester?.id || r?.requesterId || null;

function sharePreview(profile) {
  if (!profile) return [];
  const out = [];
  if (profile.shareEmail && profile.contact?.email) out.push({ icon: Mail, label: "Email", value: profile.contact.email });
  if (profile.sharePhone && profile.contactPhone) out.push({ icon: Phone, label: "Phone", value: profile.contactPhone });
  if (profile.shareWhatsapp && profile.contactWhatsapp) out.push({ icon: MessageCircle, label: "WhatsApp", value: profile.contactWhatsapp });
  if (profile.shareFacebook && profile.facebook) out.push({ icon: Users, label: "Facebook", value: profile.facebook });
  if (profile.shareInstagram && profile.instagram) out.push({ icon: Users, label: "Instagram", value: profile.instagram });
  return out;
}

export default function AssistedMatchmaking() {
  const { user } = useAuth();
  const [assistants, setAssistants] = useState(null);
  const [recs, setRecs] = useState(null);
  const [members, setMembers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [contactReqs, setContactReqs] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [label, setLabel] = useState("");
  const [lastInvite, setLastInvite] = useState(null);
  const [acceptCode, setAcceptCode] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [viewReq, setViewReq] = useState(null);
  const [approveReq, setApproveReq] = useState(null);
  const [reportReq, setReportReq] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");

  const loadAssistants = async () => { try { const { data } = await api.get("/assist/my-assistants"); setAssistants(data.assistants || []); } catch { setAssistants([]); } };
  const loadRecs = async () => { try { const { data } = await api.get("/assist/recommendations"); setRecs(data.recommendations || []); } catch { setRecs([]); } };
  const loadMembers = async () => { try { const { data } = await api.get("/assist/members"); setMembers(data.members || []); } catch { setMembers([]); } };
  const loadProposals = async () => { try { const { data } = await api.get("/assist/interest-proposals"); setProposals(data.proposals || []); } catch { setProposals([]); } };
  const loadContactReqs = async () => { try { const { data } = await api.get("/assist/contact-requests"); setContactReqs(data.requests || []); } catch { setContactReqs([]); } };
  useEffect(() => { loadAssistants(); loadRecs(); loadMembers(); loadProposals(); loadContactReqs(); }, []);

  const respondContact = async (r, action) => {
    try { await api.post(`/assist/contact-requests/${r.requestId}/${action}`, {}); await loadContactReqs(); toast.success(action === "approve" ? "Contact approved — details shared with this requester only" : "Request declined"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doApprove = async () => { const r = approveReq; setApproveReq(null); setViewReq(null); await respondContact(r, "approve"); };

  const blockRequester = async (r) => {
    const requesterId = reqId(r);
    try {
      if (requesterId) await api.post("/block", { targetId: requesterId });
      await respondContact(r, "decline");
      setViewReq(null);
      toast.success("Requester blocked and request declined");
    } catch (err) {
      try { await respondContact(r, "decline"); } catch {}
      setViewReq(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't block, but the request was declined");
    }
  };

  const submitReport = async () => {
    if (!reportReason) return toast.error("Please select a reason");
    const r = reportReq;
    const requesterId = reqId(r);
    try {
      await api.post("/report", { targetId: requesterId, reason: reportReason, details: reportDetails });
      toast.success("Report submitted. Our team will review it.");
      setReportReq(null); setReportReason(""); setReportDetails("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const respondProposal = async (p, action) => {
    try { await api.post(`/assist/interest-proposals/${p.proposalId}/${action}`, {}); await loadProposals(); toast.success(action === "approve" ? "Interest sent" : "Proposal declined"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const invite = async () => {
    setInviting(true);
    try { const { data } = await api.post("/assist/invite", { role: "wali", label: label.trim() || undefined }); setLastInvite(data); setLabel(""); await loadAssistants(); toast.success("Invitation created — share the code with your Wali"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setInviting(false); }
  };
  const revoke = async (a) => {
    if (!window.confirm("Revoke this helper's access?")) return;
    try { await api.post("/assist/revoke", { linkId: a.linkId }); await loadAssistants(); toast.success("Access revoked"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const copyCode = async (code) => { try { await navigator.clipboard.writeText(code); toast.success("Invite code copied"); } catch {} };
  const dismiss = async (r) => {
    try { await api.post(`/assist/recommendations/${r.id || r.recId}/dismiss`, {}); await loadRecs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const accept = async () => {
    if (!acceptCode.trim()) return;
    setAccepting(true);
    try { await api.post("/assist/accept", { code: acceptCode.trim().toUpperCase() }); setAcceptCode(""); await loadAssistants(); toast.success("Invitation accepted — you can now help review matches"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAccepting(false); }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="assisted-page">
      <div className="rounded-3xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-6 text-white sm:p-8">
        <h1 className="font-serif text-3xl font-bold sm:text-4xl">Assisted Matchmaking</h1>
        <p className="mt-2 text-emerald-100/90">Invite a trusted Wali to help review your matches.</p>
      </div>

      {/* Invite */}
      <div className="mt-6 rounded-3xl border border-emerald-900/10 bg-white p-6 text-center" data-testid="assisted-invite-card">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-mint"><Users className="h-6 w-6 text-emerald-800" /></div>
        <h2 className="font-serif text-xl font-bold text-emerald-900">Invite your Wali / Guardian</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">They get a private, respectful view of your curated matches. They can add notes and recommend profiles for you — they can never message anyone or see contact details.</p>
        <div className="mx-auto mt-4 flex max-w-sm flex-col gap-2 sm:flex-row">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. My father) — optional" data-testid="assisted-label-input" />
          <Button onClick={invite} disabled={inviting} data-testid="assisted-invite-btn" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><UserPlus className="mr-1.5 h-4 w-4" /> Invite your Wali</Button>
        </div>
        {lastInvite?.code && (
          <div className="mx-auto mt-4 max-w-sm rounded-2xl bg-mint/50 p-4" data-testid="assisted-invite-code">
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Share this code with your Wali</p>
            <div className="mt-2 flex items-center justify-center gap-2">
              <span className="font-mono text-2xl font-bold tracking-widest text-emerald-950">{lastInvite.code}</span>
              <button onClick={() => copyCode(lastInvite.code)} data-testid="assisted-copy-code" className="rounded-full p-1.5 text-emerald-700 hover:bg-white"><Copy className="h-4 w-4" /></button>
            </div>
            <p className="mt-2 text-xs text-slate-500">They sign in to Nikah Match and enter this code under Assisted Matchmaking → Accept an invitation.</p>
          </div>
        )}
      </div>

      {/* Contact requests received (member side) */}
      {contactReqs.length > 0 && (
        <section className="mt-8" data-testid="assisted-contactreqs-section">
          <h3 className="font-serif text-lg font-bold text-emerald-900">Contact requests</h3>
          <p className="mt-1 text-sm text-slate-500">A guardian, imam or registrar has asked to connect regarding a match. You decide.</p>
          <div className="mt-3 space-y-3" data-testid="assisted-contactreqs-list">
            {contactReqs.map((r) => (
              <div key={r.requestId} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`contactreq-${r.requestId}`}>
                <div className="flex items-center justify-between gap-3">
                  <button onClick={() => setViewReq(r)} className="flex min-w-0 items-center gap-3 text-left" data-testid={`contactreq-view-${r.requestId}`}>
                    <Avatar className="h-11 w-11 shrink-0 rounded-xl border border-amber-500/30">
                      {reqPhoto(r) && <AvatarImage src={reqPhoto(r)} alt="" className="object-cover" />}
                      <AvatarFallback className="rounded-xl bg-emerald-800 text-amber-200">{reqName(r)[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-emerald-900">{reqName(r)}</p>
                      <p className="text-xs font-medium uppercase tracking-wide text-amber-600">{reqRole(r)}</p>
                      {r.reason && <p className="mt-0.5 truncate text-sm text-slate-600">“{r.reason}”</p>}
                      <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-emerald-700"><Eye className="h-3 w-3" /> View requester details</p>
                    </div>
                  </button>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    <Button size="sm" onClick={() => setApproveReq(r)} data-testid={`contactreq-approve-${r.requestId}`} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => respondContact(r, "decline")} data-testid={`contactreq-decline-${r.requestId}`} className="rounded-full border-slate-300 text-slate-600"><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* People you're helping (Wali side) */}
      {members.length > 0 && (
        <section className="mt-8" data-testid="assisted-members-section">
          <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-emerald-900"><UserCheck className="h-5 w-5 text-emerald-700" /> People you're helping</h3>
          <p className="mt-1 text-sm text-slate-500">You've been trusted as a Wali. Review their matches and offer guidance.</p>
          <div className="mt-3 space-y-3" data-testid="assisted-members-list">
            {members.map((m) => (
              <Link key={m.id} to={`/assist/member/${m.id}`} data-testid={`member-${m.id}`} className="flex items-center justify-between rounded-2xl border border-emerald-900/10 bg-white p-4 transition-colors hover:bg-mint/40">
                <div className="flex items-center gap-3">
                  {m.photoBase64 ? <img src={m.photoBase64} alt="" className="h-11 w-11 rounded-xl object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-800 text-amber-200">{(m.displayName || "?")[0]}</div>}
                  <div>
                    <p className="font-semibold text-emerald-900">{m.displayName || m.firstName}{m.age ? `, ${m.age}` : ""}</p>
                    <p className="text-xs text-slate-500">{[m.gender, m.country].filter(Boolean).join(" · ")}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Interest proposals from your Wali (member side) */}
      {proposals.length > 0 && (
        <section className="mt-8" data-testid="assisted-proposals-section">
          <h3 className="font-serif text-lg font-bold text-emerald-900">Proposals from your Wali</h3>
          <p className="mt-1 text-sm text-slate-500">Your Wali suggested expressing interest in these matches. You decide.</p>
          <div className="mt-3 space-y-3" data-testid="assisted-proposals-list">
            {proposals.map((p) => (
              <div key={p.proposalId} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`proposal-${p.proposalId}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-emerald-900">{p.profile?.displayName || p.profile?.firstName || "A suggested match"}{p.profile?.age ? `, ${p.profile.age}` : ""}</p>
                    {p.note && <p className="mt-0.5 text-sm text-slate-500">“{p.note}”</p>}
                    {p.byName && <p className="text-xs text-slate-400">Proposed by {p.byName}{p.byRole ? ` (${p.byRole})` : ""}</p>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" onClick={() => respondProposal(p, "approve")} data-testid={`proposal-approve-${p.proposalId}`} className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Check className="mr-1 h-3.5 w-3.5" /> Send interest</Button>
                    <Button size="sm" variant="outline" onClick={() => respondProposal(p, "decline")} data-testid={`proposal-decline-${p.proposalId}`} className="rounded-full border-slate-300 text-slate-600"><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Your helpers */}
      <section className="mt-8">
        <h3 className="font-serif text-lg font-bold text-emerald-900">Your helpers</h3>
        {assistants === null ? <Skeleton className="mt-3 h-20 rounded-2xl" />
          : assistants.length === 0 ? <p className="mt-2 text-sm text-slate-500" data-testid="assisted-no-helpers">No one is helping you yet. Invite your Wali above, or an admin can assign a matchmaker.</p>
          : (
            <div className="mt-3 space-y-3" data-testid="assisted-helpers-list">
              {assistants.map((a) => (
                <div key={a.linkId} className="flex items-center justify-between rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`helper-${a.linkId}`}>
                  <div>
                    <p className="font-semibold text-emerald-900">{a.assistantName || a.label || a.roleLabel || "Wali"}</p>
                    <p className="text-xs text-slate-500">{a.roleLabel || a.role}{a.status === "pending" && a.code ? ` · code ${a.code}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${statusBadge[a.status] || "bg-slate-100 text-slate-600"}`}>{a.status}</span>
                    {a.status === "pending" && a.code && <button onClick={() => copyCode(a.code)} className="rounded-full p-1.5 text-emerald-700 hover:bg-mint" title="Copy code" data-testid={`helper-copy-${a.linkId}`}><Copy className="h-4 w-4" /></button>}
                    <button onClick={() => revoke(a)} className="rounded-full p-1.5 text-red-500 hover:bg-red-50" title="Revoke" data-testid={`helper-revoke-${a.linkId}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </section>

      {/* Recommended for you */}
      <section className="mt-8">
        <h3 className="font-serif text-lg font-bold text-emerald-900">Recommended for you</h3>
        {recs === null ? <Skeleton className="mt-3 h-20 rounded-2xl" />
          : recs.length === 0 ? <p className="mt-2 text-sm text-slate-500" data-testid="assisted-no-recs">When your Wali or matchmaker recommends someone, it will appear here.</p>
          : (
            <div className="mt-3 space-y-3" data-testid="assisted-recs-list">
              {recs.map((r) => {
                const pid = r.profileId || r.profile?.id || r.targetId;
                const name = r.profile?.name || r.name || r.displayName || "A suggested match";
                return (
                  <div key={r.id || r.recId || pid} className="flex items-center justify-between rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`rec-${r.id || pid}`}>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 font-semibold text-emerald-900"><Heart className="h-4 w-4 text-amber-500" /> {name}</p>
                      {r.note && <p className="mt-0.5 truncate text-sm text-slate-500">“{r.note}”</p>}
                      {r.byName && <p className="text-xs text-slate-400">Recommended by {r.byName}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {pid && <Link to={`/profiles/${pid}`}><Button size="sm" className="rounded-full bg-emerald-800 text-white" data-testid={`rec-view-${r.id || pid}`}>View</Button></Link>}
                      <button onClick={() => dismiss(r)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" title="Dismiss" data-testid={`rec-dismiss-${r.id || pid}`}><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </section>

      {/* Accept an invitation (for people invited as a Wali) */}
      <section className="mt-8 rounded-2xl border border-dashed border-emerald-300 bg-mint/30 p-5" data-testid="assisted-accept-card">
        <p className="flex items-center gap-2 font-semibold text-emerald-900"><Ticket className="h-5 w-5" /> Were you invited as a Wali?</p>
        <p className="mt-1 text-sm text-slate-500">Enter the invite code you received to start helping someone review their matches.</p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input value={acceptCode} onChange={(e) => setAcceptCode(e.target.value)} placeholder="Invite code (e.g. 5F7F2414)" className="font-mono uppercase" data-testid="assisted-accept-input" />
          <Button onClick={accept} disabled={accepting || !acceptCode.trim()} data-testid="assisted-accept-btn" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">Accept invitation</Button>
        </div>
      </section>

      {/* Requester details dialog */}
      <Dialog open={!!viewReq} onOpenChange={(o) => !o && setViewReq(null)}>
        <DialogContent className="max-w-md" data-testid="requester-detail-dialog">
          <DialogHeader><DialogTitle>About this requester</DialogTitle></DialogHeader>
          {viewReq && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-16 w-16 rounded-2xl border border-amber-500/30">
                  {reqPhoto(viewReq) && <AvatarImage src={reqPhoto(viewReq)} alt="" className="object-cover" />}
                  <AvatarFallback className="rounded-2xl bg-emerald-800 text-amber-200">{reqName(viewReq)[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-serif text-lg font-bold text-emerald-900" data-testid="requester-name">{reqName(viewReq)}</p>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">{reqRole(viewReq)}</p>
                  {viewReq.requester?.professionalVerified
                    ? <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800" data-testid="requester-verified"><ShieldCheck className="h-3 w-3" /> Verified</span>
                    : <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500" data-testid="requester-unverified">Role not independently verified</span>}
                </div>
              </div>
              {[viewReq.requester?.city, viewReq.requester?.country].filter(Boolean).length > 0 && (
                <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-emerald-700" /> {[viewReq.requester?.city, viewReq.requester?.country].filter(Boolean).join(", ")}</p>
              )}
              {viewReq.requester?.organization && <p className="flex items-center gap-2 text-sm text-slate-600"><Building2 className="h-4 w-4 text-emerald-700" /> {viewReq.requester.organization}</p>}
              {viewReq.requester?.bio && <p className="rounded-xl bg-ivory p-3 text-sm text-slate-700" data-testid="requester-bio">{viewReq.requester.bio}</p>}
              <div className="rounded-xl bg-mint/50 p-3 text-sm text-emerald-900/90">
                <p><span className="font-semibold">Helping:</span> {HELPING_LABEL[viewReq.helpingWho] || viewReq.helpingWho || "—"}</p>
                {viewReq.reason && <p className="mt-1"><span className="font-semibold">Reason:</span> “{viewReq.reason}”</p>}
                {viewReq.intro && <p className="mt-1">{viewReq.intro}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setApproveReq(viewReq)} data-testid="requester-approve" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => { respondContact(viewReq, "decline"); setViewReq(null); }} data-testid="requester-decline" className="rounded-full border-slate-300 text-slate-600"><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                <Button size="sm" variant="outline" onClick={() => { setReportReq(viewReq); setViewReq(null); }} data-testid="requester-report" className="rounded-full border-amber-400/50 text-amber-700 hover:bg-amber-50"><Flag className="mr-1 h-3.5 w-3.5" /> Report</Button>
                <Button size="sm" variant="outline" onClick={() => blockRequester(viewReq)} data-testid="requester-block" className="rounded-full border-red-300 text-red-600 hover:bg-red-50"><Ban className="mr-1 h-3.5 w-3.5" /> Block</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve confirmation with release preview */}
      <Dialog open={!!approveReq} onOpenChange={(o) => !o && setApproveReq(null)}>
        <DialogContent className="max-w-md" data-testid="approve-confirm-dialog">
          <DialogHeader><DialogTitle>Share your contact details?</DialogTitle></DialogHeader>
          {approveReq && (() => {
            const preview = sharePreview(user?.profile);
            const who = reqName(approveReq);
            return (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Approving releases the following to <strong>{who}</strong> only. It does not share them with anyone else.</p>
                {preview.length > 0 ? (
                  <div className="space-y-2 rounded-xl bg-mint/50 p-3" data-testid="release-preview">
                    {preview.map((p, i) => <p key={i} className="flex items-center gap-2 text-sm text-emerald-900"><p.icon className="h-4 w-4 text-emerald-700" /> <span className="font-medium">{p.label}:</span> {p.value}</p>)}
                  </div>
                ) : (
                  <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-200" data-testid="release-preview-empty">
                    You haven't chosen any contact details to share yet. Set what you share under <Link to="/profile" className="font-semibold underline">My Profile → Contact sharing</Link> first, or approve to allow the app's default contact fields.
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveReq(null)}>Cancel</Button>
            <Button onClick={doApprove} data-testid="approve-confirm-btn" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Check className="mr-1.5 h-4 w-4" /> Approve &amp; share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report requester */}
      <Dialog open={!!reportReq} onOpenChange={(o) => !o && setReportReq(null)}>
        <DialogContent data-testid="report-requester-dialog">
          <DialogHeader><DialogTitle>Report this requester</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger data-testid="report-requester-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>{["Fake or impersonation", "Inappropriate behaviour", "Harassment", "Scam / spam", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Additional details (optional)" data-testid="report-requester-details" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportReq(null)}>Cancel</Button>
            <Button onClick={submitReport} data-testid="report-requester-submit" className="bg-red-600 text-white hover:bg-red-700">Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
